import { isGlobalManager } from "../forms/formSource";
import type { FormGrantRole } from "../../types/formGrant";
import type {
  OrganisationRole,
  UserOrganisation,
} from "../../types/organisation";

export type { OrganisationRole, UserOrganisation, FormGrantRole };

export type FormGrantAccess = {
  formId?: number | null;
  grantRole?: FormGrantRole | null;
  grantedFormIds?: Iterable<number> | null;
  manageGrantedFormIds?: Iterable<number> | null;
};

function idSet(ids: Iterable<number> | null | undefined): Set<number> {
  return ids instanceof Set ? ids : new Set(ids ?? []);
}

function hasGrantedForm(
  ids: Iterable<number> | null | undefined,
  formId: number | null | undefined,
): boolean {
  if (formId == null) return false;
  return idSet(ids).has(formId);
}

export function getOrganisationMembership(
  organisations: UserOrganisation[],
  organisationId: number | null | undefined,
): UserOrganisation | null {
  if (organisationId == null) return null;
  return organisations.find((org) => org.id === organisationId) ?? null;
}

export function canManageOrganisations(roles: string[]): boolean {
  return isGlobalManager(roles);
}

export function canManageOrgMembers(
  roles: string[],
  organisations: UserOrganisation[],
  organisationId: number,
): boolean {
  if (isGlobalManager(roles)) return true;
  const membership = getOrganisationMembership(organisations, organisationId);
  return membership?.role === "org_admin";
}

export function canCreateForms(
  roles: string[],
  organisations: UserOrganisation[],
): boolean {
  if (isGlobalManager(roles)) return true;
  return organisations.some(
    (org) => org.role === "org_admin" || org.role === "form_manager",
  );
}

/** CRUD формы, answer-share, удаление ответов, выдача грантов. */
export function canManageForm(
  roles: string[],
  organisations: UserOrganisation[],
  organisationId: number | null | undefined,
  access?: FormGrantAccess,
): boolean {
  if (isGlobalManager(roles)) return true;
  if (access?.grantRole === "manage") return true;
  if (hasGrantedForm(access?.manageGrantedFormIds, access?.formId)) return true;
  if (organisationId == null) return false;

  const membership = getOrganisationMembership(organisations, organisationId);
  return (
    membership?.role === "org_admin" || membership?.role === "form_manager"
  );
}

/** Просмотр формы и ответов (form_viewer+ или грант). */
export function canViewForm(
  roles: string[],
  organisations: UserOrganisation[],
  organisationId: number | null | undefined,
  access?: FormGrantAccess,
): boolean {
  if (isGlobalManager(roles)) return true;
  if (access?.grantRole === "manage" || access?.grantRole === "view") {
    return true;
  }
  if (hasGrantedForm(access?.grantedFormIds, access?.formId)) return true;
  if (hasGrantedForm(access?.manageGrantedFormIds, access?.formId)) return true;
  if (organisationId == null) return false;
  return getOrganisationMembership(organisations, organisationId) != null;
}

/** Просмотр и экспорт ответов. */
export function canViewSubmissions(
  roles: string[],
  organisations: UserOrganisation[],
  organisationId: number | null | undefined,
  access?: FormGrantAccess,
): boolean {
  return canViewForm(roles, organisations, organisationId, access);
}

/** Открыть конструктор: роль в орг, глобальный менеджер или хотя бы один грант. */
export function canUseManagementTools(
  roles: string[],
  organisations: UserOrganisation[],
  grantedFormIds?: Iterable<number> | null,
): boolean {
  if (isGlobalManager(roles)) return true;
  if (organisations.length > 0) return true;
  return idSet(grantedFormIds).size > 0;
}

export function organisationRoleLabel(role: OrganisationRole): string {
  switch (role) {
    case "org_admin":
      return "Админ организации";
    case "form_manager":
      return "Менеджер форм";
    case "form_viewer":
      return "Просмотр";
    default:
      return role;
  }
}
